import { GAEvents } from "@js/ga_events"

/**
 * Represents a Google Analytics event with its associated properties
 */
export type GAEvent = {
  /** The name of the event to be tracked */
  eventName: string
  /** The category under which the event falls */
  eventCategory: string
  /** Additional data to be sent with the event */
  data?: Record<string, any>
  /** Optional user ID to associate with the event */
  userId?: string | null
  /** Optional error type if the event is related to an error */
  errorType?: string
}

/**
 * Represents a funnel event, which is a specialized type of GA event
 * that includes funnel-specific properties
 */
export type FunnelEvent = GAEvent & {
  /** The name of the funnel being tracked */
  funnelName: string
  /** The current step number in the funnel */
  funnelStep: number
  /** The name of the current step in the funnel */
  funnelStepName: string
}

/**
 * Sends a Google Analytics event with the specified properties
 * @param {GAEvent} params - The event parameters
 * @param {string} params.eventName - The name of the event
 * @param {string} params.eventCategory - The category of the event
 * @param {Record<string, any>} [params.data=Record<string, any>] - Additional data to send with the event
 * @param {string} [params.userId] - Optional user ID
 * @param {string} [params.errorType] - Optional error type
 */
export const sendGAEvent = ({
  eventName,
  eventCategory,
  data = {},
  userId,
  errorType,
}: GAEvent) => {
  GAEvents.enqueueGAEvent(eventName, eventCategory, {
    user_id: userId || window.currentUser?.id,
    error_type: errorType,
    ...data,
  })
}

/**
 * Sends a funnel-specific Google Analytics event
 * @param {FunnelEvent} params - The funnel event parameters
 * @param {string} params.eventName - The name of the event
 * @param {string} params.eventCategory - The category of the event
 * @param {Record<string, any>} [params.data={}] - Additional data to send with the event
 * @param {string} [params.userId] - Optional user ID
 * @param {string} params.funnelName - The name of the funnel
 * @param {number} params.funnelStep - The current step number in the funnel
 */
export const sendFunnelEvent = ({
  eventName,
  eventCategory,
  data = {},
  userId,
  funnelName,
  funnelStep,
  funnelStepName,
}: FunnelEvent) => {
  sendGAEvent({
    eventName,
    eventCategory,
    userId,
    data: {
      ...data,
      funnel_name: funnelName,
      funnel_step: funnelStep,
      funnel_step_name: funnelStepName,
    },
  })
}
